import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import { gr } from "../tokens";

const clamp = (n) => Math.max(0, Math.min(100, Number(n) || 0));

// Linear progress: brand fill on a light track. Optional label (left) and value text (right).
export function GrProgressBar({
  value,
  label,
  valueText,
  thick = false,
  ariaLabel,
  sx,
}) {
  const v = clamp(value);
  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: gr.space[1],
        minWidth: 0,
        ...sx,
      }}
    >
      {label || valueText ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            gap: gr.space[3],
            fontSize: gr.font.size.xs,
          }}
        >
          <Box component="span" sx={{ color: gr.color.muted }}>
            {label}
          </Box>
          <Box
            component="span"
            sx={{
              color: gr.color.ink2,
              fontWeight: gr.font.weight.semibold,
              fontVariantNumeric: "tabular-nums",
            }}
          >
            {valueText}
          </Box>
        </Box>
      ) : null}
      <Box
        role="progressbar"
        aria-label={
          ariaLabel || (typeof label === "string" ? label : undefined)
        }
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(v)}
        sx={{
          height: thick ? 8 : 6,
          borderRadius: gr.radius.pill,
          background: gr.color.line2,
          overflow: "hidden",
        }}
      >
        <Box
          sx={{
            width: `${v}%`,
            height: "100%",
            borderRadius: gr.radius.pill,
            background: gr.color.brand,
          }}
        />
      </Box>
    </Box>
  );
}

GrProgressBar.propTypes = {
  value: PropTypes.number.isRequired,
  label: PropTypes.node,
  valueText: PropTypes.node,
  thick: PropTypes.bool,
  ariaLabel: PropTypes.string,
  // Token colour for the filled arc (default brand), e.g. gr.color.ok.
  color: PropTypes.string,
  sx: PropTypes.object,
};

// SVG colours go in `style` (not presentation attributes) so CSS variables resolve in every browser.
// Progress ring: brand arc for done, optional grey arc after it (e.g. missed), centre value + caption.
export function GrProgressRing({
  value,
  secondary = 0,
  size = 128,
  stroke = 12,
  centre,
  caption,
  ariaLabel,
  color,
}) {
  const r = (size - stroke) / 2;
  const c = 2 * Math.PI * r;
  const done = (clamp(value) / 100) * c;
  const second = (clamp(secondary) / 100) * c;
  const mid = size / 2;
  return (
    <Box
      component="svg"
      viewBox={`0 0 ${size} ${size}`}
      role="img"
      aria-label={ariaLabel || `${Math.round(clamp(value))}% complete`}
      sx={{
        width: size,
        height: size,
        flex: "none",
        fontFamily: gr.font.family,
      }}
    >
      <circle
        cx={mid}
        cy={mid}
        r={r}
        fill="none"
        style={{ stroke: gr.color.line2 }}
        strokeWidth={stroke}
      />
      {second > 0 ? (
        <circle
          cx={mid}
          cy={mid}
          r={r}
          fill="none"
          style={{ stroke: gr.color.lineStrong }}
          strokeWidth={stroke}
          strokeDasharray={`${second} ${c}`}
          strokeDashoffset={-done}
          transform={`rotate(-90 ${mid} ${mid})`}
        />
      ) : null}
      <circle
        cx={mid}
        cy={mid}
        r={r}
        fill="none"
        style={{
          stroke: color || gr.color.brand,
          transition: "stroke-dasharray .6s ease",
        }}
        strokeWidth={stroke}
        strokeLinecap={done > 0 ? "round" : "butt"}
        strokeDasharray={`${done} ${c}`}
        transform={`rotate(-90 ${mid} ${mid})`}
      />
      <text
        x={mid}
        y={caption ? mid - 2 : mid + 8}
        textAnchor="middle"
        fontSize={size * 0.2}
        fontWeight="600"
        style={{ fill: gr.color.ink }}
      >
        {centre !== undefined ? centre : `${Math.round(clamp(value))}%`}
      </text>
      {caption ? (
        <text
          x={mid}
          y={mid + size * 0.14}
          textAnchor="middle"
          fontSize={size * 0.09}
          style={{ fill: gr.color.muted }}
        >
          {caption}
        </text>
      ) : null}
    </Box>
  );
}

GrProgressRing.propTypes = {
  value: PropTypes.number.isRequired,
  secondary: PropTypes.number,
  size: PropTypes.number,
  stroke: PropTypes.number,
  centre: PropTypes.node,
  caption: PropTypes.string,
  ariaLabel: PropTypes.string,
};
